import React from 'react';
import { RefreshCcw } from 'lucide-react';
import SEO from '../components/SEO';

const Refund = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-24 pb-16">
      <SEO
        title="Refund Policy — Jerusalem Holy Prayer"
        description="Our 30-day money-back guarantee, when refunds are granted, and how to request one."
        path="/refund"
      />
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-gradient-to-br from-amber-500 to-amber-600 rounded-full shadow-xl">
                <RefreshCcw className="h-12 w-12 text-white" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-serif font-bold text-gray-900 mb-3">Refund Policy</h1>
            <p className="text-gray-600">Effective Date: June 1, 2026</p>
          </div>

          {/* Content card */}
          <div data-testid="refund-content" className="bg-white rounded-xl shadow-lg p-8 md:p-12 space-y-10">

            <section>
              <p className="text-gray-700 leading-relaxed">
                Because your prayer is a deeply personal offering, we want you to feel completely at peace with
                your decision to entrust it to us. This policy outlines when refunds are offered, how to request
                one, and what to do if you are not satisfied with our service.
              </p>
            </section>

            <section className="border-l-4 border-amber-500 bg-amber-50/60 rounded-r-lg pl-5 py-4">
              <p className="text-gray-800 italic leading-relaxed">
                “If your prayer has not yet been delivered to Jerusalem, you may cancel and receive a full
                refund at any time — no questions asked.”
              </p>
            </section>

            {/* 30-day Guarantee */}
            <section>
              <h2 className="text-2xl font-serif font-bold text-gray-900 mb-4">1. Our 30-Day Money-Back Guarantee</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                Every prayer submitted to Jerusalem Holy Prayer is covered by a <strong>30-day money-back
                guarantee</strong>, effective from the date of your purchase.
              </p>
              <p className="text-gray-700 leading-relaxed">
                If, for any reason, you are unsatisfied with our service — whether because your prayer was not
                delivered, the photo proof did not arrive, or you simply changed your mind — you may request a
                full refund within 30 days of your purchase date.
              </p>
            </section>

            {/* When refunds apply */}
            <section>
              <h2 className="text-2xl font-serif font-bold text-gray-900 mb-4">2. When a Full Refund Is Granted</h2>
              <ul className="list-disc list-inside space-y-2 text-gray-700 leading-relaxed">
                <li>Your prayer was purchased but has not yet been physically delivered to any holy site.</li>
                <li>You submitted your request in error (wrong plan, duplicate order, etc.).</li>
                <li>You did not receive your delivery confirmation email within 14 days of purchase.</li>
                <li>The photo proof of delivery was never received.</li>
                <li>Any technical or payment issue on our end that prevented fulfillment.</li>
              </ul>
            </section>

            {/* When refunds may be partial */}
            <section>
              <h2 className="text-2xl font-serif font-bold text-gray-900 mb-4">3. Partial Refunds</h2>
              <p className="text-gray-700 leading-relaxed">
                If your prayer has already been printed, delivered, and photographed at one or more sites at
                the time of your refund request, a partial refund may be offered at our discretion. In such
                cases we will discuss the situation with you directly to reach a fair resolution.
              </p>
            </section>

            {/* When refunds don't apply */}
            <section>
              <h2 className="text-2xl font-serif font-bold text-gray-900 mb-4">4. When a Refund Cannot Be Issued</h2>
              <ul className="list-disc list-inside space-y-2 text-gray-700 leading-relaxed">
                <li>Requests made more than 30 days after the original purchase date.</li>
                <li>Ongoing subscription payments already fulfilled (see &ldquo;Subscription Cancellations&rdquo; below for
                how to stop future charges).</li>
                <li>Change of heart after the scroll has been physically hand-delivered to the requested holy
                sites and photo proof has been sent.</li>
              </ul>
            </section>

            {/* Subscriptions */}
            <section>
              <h2 className="text-2xl font-serif font-bold text-gray-900 mb-4">5. Subscription Cancellations</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                Subscriptions (Prayer Journey plans) can be cancelled at any time. Once cancelled, no further
                charges will occur, but past monthly payments for prayers that have already been delivered are
                non-refundable.
              </p>
              <p className="text-gray-700 leading-relaxed">
                To cancel a subscription, please email us at{' '}
                <a href="mailto:info@jerusalemholyprayer.org" className="text-amber-700 underline hover:text-amber-800">
                  info@jerusalemholyprayer.org
                </a>{' '}
                with your name and the email address used at checkout.
              </p>
            </section>

            {/* How to request */}
            <section>
              <h2 className="text-2xl font-serif font-bold text-gray-900 mb-4">6. How to Request a Refund</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                Refund requests are simple. Send an email to{' '}
                <a href="mailto:info@jerusalemholyprayer.org" className="text-amber-700 underline hover:text-amber-800">
                  info@jerusalemholyprayer.org
                </a>{' '}
                with the following details:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 leading-relaxed">
                <li>The name and email used at checkout</li>
                <li>Your prayer reference number (if you received one)</li>
                <li>The reason for your refund request</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-3">
                We aim to review all requests within 3 business days. Approved refunds are processed to your
                original payment method within 5–10 business days, depending on your bank.
              </p>
            </section>

            {/* Processing */}
            <section>
              <h2 className="text-2xl font-serif font-bold text-gray-900 mb-4">7. Refund Processing Times</h2>
              <p className="text-gray-700 leading-relaxed">
                Once approved, refunds are issued through our payment processor (Stripe) back to the original
                card or payment method. Please allow 5–10 business days for the funds to appear in your
                account. If you do not see the refund after 10 business days, contact your bank first, then
                reach out to us with the transaction reference and we will investigate immediately.
              </p>
            </section>

            {/* Chargebacks */}
            <section>
              <h2 className="text-2xl font-serif font-bold text-gray-900 mb-4">8. A Note on Chargebacks</h2>
              <p className="text-gray-700 leading-relaxed">
                Please contact us before initiating a chargeback with your bank. We are a small, prayer-driven
                team and we will always try to resolve any issue fairly and quickly if you speak to us first.
                Chargebacks incur significant fees and administrative burden, and we would rather solve the
                problem together.
              </p>
            </section>

            {/* Contact */}
            <section>
              <h2 className="text-2xl font-serif font-bold text-gray-900 mb-4">9. Contact Us</h2>
              <p className="text-gray-700 leading-relaxed">
                For any question, concern, or refund request, please email{' '}
                <a href="mailto:info@jerusalemholyprayer.org" className="text-amber-700 underline hover:text-amber-800">
                  info@jerusalemholyprayer.org
                </a>
                . We respond to all messages within 24 hours (business days). Every prayer, and every person
                behind it, matters to us.
              </p>
            </section>

            {/* Closing */}
            <section className="border-t border-gray-200 pt-8 text-center">
              <p className="text-gray-600 italic leading-relaxed">
                &ldquo;May the God of hope fill you with all joy and peace as you trust in him.&rdquo;
                <br />
                <span className="text-sm">— Romans 15:13</span>
              </p>
            </section>

          </div>
        </div>
      </div>
    </div>
  );
};

export default Refund;
